import * as React from 'react';

import { AffectedFamilyPageProps } from "./affected-family-page.types";
import { FieldSelectorOnChangeMultiple } from '@common/components/FieldSelector/field-selector.types';
import { I18n } from 'react-redux-i18n';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { FieldType } from '@common/dto/field/type';
import { QuestionnaireSubfieldUpdateDto } from '@common/dto/questionnaire/field/subfield-update-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireAffectedFamilyPageUpdateDto } from '@common/dto/questionnaire/page/affected-family/update-dto';
import { FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT } from '@modules/fhq-design-tool/constants/shared';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';

export const useAffectedFamilyPageController = (props: AffectedFamilyPageProps) => {
  const { data, onChange, showErrorMessage, cachedData, getPage, getFhq4QuestionnaireField, questionnaire, errors } = props;
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);
  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [loadingPage, setLoadingPage] = React.useState(false);

  const initializeData = async () => {
    setLoadingPage(true);

    if (!cachedData) {
      try {
        await getPage(data.id);
      } catch (err) {
        console.error(err);
        showErrorMessage(I18n.t('Failed to get page'))
      }
    }
    
    setLoadingPage(false)
  };

  React.useEffect(() => {
    initializeData();
  }, [data.id]);

  const updatePage = React.useCallback((dataToUpdate: Partial<QuestionnaireAffectedFamilyPageUpdateDto>) => {
    if (cachedData) {
      onChange({
        ...cachedData,
        ...dataToUpdate,
        modified: true,
      });
    }
  }, [cachedData, onChange]);

  const handlePageNameChange = React.useCallback((val: string) => {
    updatePage({
      title: val.trim()
    });
  }, [updatePage]);

  const handlePageDescriptionChange = React.useCallback((val: string) => {
    updatePage({
      description: val
    });
  }, [updatePage]);

  type FieldInList = {
    id: string;
    name: string;
    original: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto;
  };

  const handleFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    updatePage({
      fields: newFieldsOrder.map((x, i) => ({
        ...x.original,
        order: i + 1,
      }))
    });
  }, [updatePage]);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
  }, []);

  const handleFieldAdd = React.useCallback(
    async ({ fields }: FieldSelectorOnChangeMultiple) => {
      const newFields: Array<QuestionnaireFieldAddDto | QuestionnaireFieldListDto> = [];
      const existingFieldsLength = cachedData?.fields?.length || 0;

      await Promise.all(
        fields.map(async (field, fieldIndex) => {
          const hasSameField = cachedData?.fields.some(
            (x) =>
              getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
              getUniqueFieldCombination({ id: field.fieldId, context: field.fieldContext, type: field.fieldTypeId })
          );

          if (hasSameField || !cachedData) {
            return;
          }

          try {
            const fullField = await getFhq4QuestionnaireField({
              id: field.fieldId,
              context: field.fieldContext,
              system: field.systemField,
            });
            
            const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
              progenyId: field.fieldId as number,
              name: field.fieldName,
              path: fullField.path || fullField.name,
              order: existingFieldsLength + fieldIndex + 1,
              systemField: field.systemField,
              context: field.fieldContext,
              type: fullField.type,
              progenyFieldType: field.fieldTypeId as FieldType,
              description: fullField.description,
              required: fullField.required,
              defaultValue: fullField.defaultValue,
              allowDuplications: fullField.allowDuplications,
              skipLogic: fullField.skipLogic,
              options: fullField.options,
              subfields: fullField.subfields,
              skipLogicEnabled: fullField.skipLogicEnabled,
              displayInFHQ: fullField.displayInFHQ,
              possibleDropdownOptions: fullField.possibleDropdownOptions,
            };

            newFields.push(newField);
          } catch (err) {
            console.error(err);
            showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.fieldName })}`);
          }
        })
      );

      if (!newFields.length) {
        return;
      }

      updatePage({
        fields: [...(cachedData?.fields || []), ...newFields],
      });
    },
    [cachedData, updatePage, showErrorMessage, getFhq4QuestionnaireField]
  );

  const handleFieldDelete = React.useCallback((field: FieldInList) => {
    updatePage({
      fields: cachedData?.fields
        ?.filter((x) => 
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) !== getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType })
        )
        .sort((a, b) => a.order - b.order)
        .map((f, i) => ({
          ...f,
          order: i + 1,
        }))
    });
  }, [cachedData, updatePage]);

  const handleFieldChange = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireSubfieldUpdateDto | QuestionnaireFieldAddDto) => {
    updatePage({
      fields: cachedData?.fields?.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType }) 
          ? field 
          : x
      ),
    });
  }, [cachedData, updatePage]);

  const navigateOnFieldEdit = React.useCallback((field: FieldInList) => {
    setEditingFieldUniqueCombination(getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType }));
  }, []);

  const goBackFromFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
  }, []);

  const isFirstNameField = React.useCallback((field: QuestionnaireFieldAddDto | QuestionnaireFieldUpdateDto) => {
    if (!questionnaire.homeTab.firstNameFieldId) {
      return false;
    }

    const result =
      getUniqueFieldCombination({
        id: questionnaire.homeTab.firstNameFieldId,
        system: questionnaire.homeTab.firstNameFieldSystem,
        context: FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT,
      }) ===
      getUniqueFieldCombination({
        id: field.progenyId,
        system: field.systemField,
        context: field.context,
      });

    return result;
  }, [questionnaire.homeTab.firstNameFieldId, questionnaire.homeTab.firstNameFieldSystem]);

  const canChangeFieldOrder = React.useCallback((field: FieldInList) => {
    return !isFirstNameField(field.original);
  }, [isFirstNameField]);

  const canDeleteField = React.useCallback((field: FieldInList) => {
    return !isFirstNameField(field.original);
  }, [isFirstNameField]);

  const canShowHideField = React.useCallback(() => {
    return false;
  }, []);

  const fieldHasError = React.useCallback((field: FieldInList) => {
    return errors?.fields ? Boolean(errors?.fields[field.original.progenyId]) : false;
  }, [errors]);

  const fields = cachedData?.fields || [];
  const editingField = fields.find((x) => getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === editingFieldUniqueCombination);

  const processedFields = React.useMemo(() => {
    return fields
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [fields]);

  return {
    fieldHasError,
    canChangeFieldOrder,
    canDeleteField,
    isFirstNameField,
    processedFields,
    loadingPage,
    handleFieldChange,
    editingField,
    goBackFromFieldEdit,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldAdd,
    handlePageNameChange,
    handlePageDescriptionChange,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
  };
};